import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Url } from '@rossum/api-client';
import { CustomRelation } from '@rossum/api-client/relations';
import {
  AnnotationStatus,
  MetaColumn,
  MetaField,
} from '@rossum/api-client/shared';
import { camelToSnake, snakeToCamel } from '../../lib/keyConvertor';
import { CustomRelationBadges } from '../custom-relations/columns/CustomRelationBadges';
import { useCustomRelationsConfig } from '../custom-relations/hooks/useCustomRelationsConfig';
import {
  SelectAnnotationParams,
  SupportedAnnotationView,
} from '../document-list-base/supportedAnnotationViews';
import { useRequestUnpaginatedLabels } from '../labels/hooks/useRequestLabels';
import { detailsColumnOptionKeys } from '../mql/constants';
import {
  detailsOperators,
  exactStringFieldOperators,
  multiSelectOperators,
  queueSelectOperators,
  statusOperators,
  stringFieldOperators,
} from '../mql/operators';
import {
  eInvoicingFeatureSelector,
  labelsFeatureSelector,
} from '../pricing/selectors';
import { useWorkspacesWithQueues } from '../queues/hooks/useWorkspacesWithQueues';
import { LabelsColumn } from './components/LabelsColumn';
import { RenderDetails, RenderDetailsRow } from './components/RenderDetails';
import { RenderFilenameCell } from './components/RenderFilenameCell';
import { RenderIdentifier } from './components/RenderIdentifier';
import { RenderStatus } from './components/RenderStatus';
import { RenderTrainingStatus } from './components/RenderTrainingStatus';
import { RenderWorkflowFilenameCell } from './components/RenderWorkflowFilenameCell';
import { WorkspacesQueuesCell } from './components/WorkspacesQueuesCell';
import { allTabStatuses } from './status/helpers';
import { isStatusVisible } from './status/types';
import { MetaColDef, ValueOptions } from './TypedGridColDef';

type UseMetaColumnsParams<RowType> = {
  statusColumn?: Record<never, never>;
  documentColumn?: {
    setRowForPreview?: (row: RowType | undefined) => void;
  };
  workflowFilenameColumn?: {
    setRowForPreview?: (row: RowType | undefined) => void;
  };
  labelsColumn?: Record<never, never>;
  annotationColumn?: Record<never, never>;
  queueColumn?: {
    level: string | undefined;
  };
  detailsColumn?: {
    handleSelectAnnotation?: (params: {
      annotationUrl: string;
      view: SupportedAnnotationView;
    }) => void;
  };
  relationsColumn?: {
    handleSelectAnnotation?: (params: SelectAnnotationParams) => void;
  };
  trainingColumn?: Record<never, never>;
};

// TODO: Copy pasted from document-list, we should get rid of this completely.
const toDict = <T extends { url: string }>(
  items: T[] = [],
  // TODO: might be cool to allow only keys of T which values are strings or numbers
  keyProp: keyof T = 'url'
) => R.indexBy(items, item => String(item[keyProp]));

const sortValueOptions = (valueOptions: ValueOptions) =>
  valueOptions.sort((a, b) => a.label.localeCompare(b.label));

// TODO: fix typings of allTabStatuses
const supportedStatuses = allTabStatuses.filter(isStatusVisible);

const commonMetaColumnProps: Omit<MetaColumn, 'metaName' | 'width'> = {
  columnType: 'meta',
  visible: true,
};

export type MetaColumnBase = {
  labels: Url[];
  modifier:
    | {
        id: number;
        username: string;
      }
    | string;
  id: number;
  status: AnnotationStatus;
  pages: unknown[];
  url: Url;
  restricted_access?: boolean;
  hasWorkflowRun: boolean;
  training_enabled?: boolean;
  customRelations?: CustomRelation[];
} & RenderDetailsRow;

export const useMetaColumns = <RowType extends MetaColumnBase>({
  statusColumn,
  documentColumn,
  workflowFilenameColumn,
  labelsColumn,
  queueColumn,
  detailsColumn,
  relationsColumn,
  annotationColumn,
  trainingColumn,
}: UseMetaColumnsParams<RowType>): { columns: Array<MetaColDef<RowType>> } => {
  const intl = useIntl();
  const eInvoicingEnabled = useSelector(eInvoicingFeatureSelector);
  const { isEnabled: customRelationsEnabled } = useCustomRelationsConfig();

  const labelsEnabled = useSelector(labelsFeatureSelector);
  const { data: allLabels, isInitialLoading: allLabelsAreLoading } =
    useRequestUnpaginatedLabels({
      select: toDict,
    });

  const { workspacesWithQueues: workspaces, isLoading: workspacesAreLoading } =
    useWorkspacesWithQueues({
      enableQueries: true,
    });

  const queueValuesArray = workspaces?.flatMap(workspace => {
    const queueValues = workspace.queues.map(queue => ({
      url: queue.url,
      queueId: queue.id,
      queueName: queue.name,
      workspaceName: workspace.name,
    }));

    return queueValues;
  });

  const workspacesWithQueuesMap = toDict(queueValuesArray);

  const columns = R.pipe<
    Array<MetaColDef<RowType> | undefined>,
    Array<MetaColDef<RowType>>
  >(
    [
      annotationColumn !== undefined
        ? {
            ...commonMetaColumnProps,
            metaName: 'annotation',
            width: 170,
            field: 'annotation' satisfies MetaField,
            headerName: intl.formatMessage({
              id: 'components.documentOverview.annotation',
            }),
            visible: false,
            renderCell: RenderIdentifier,
            operators: exactStringFieldOperators,
            filterable: true,
          }
        : undefined,
      statusColumn !== undefined
        ? {
            ...commonMetaColumnProps,
            metaName: 'status',
            width: 170,
            field: 'status' satisfies MetaField,
            headerName: intl.formatMessage({
              id: 'components.documentOverview.status',
            }),
            renderCell: RenderStatus,
            operators: statusOperators,
            valueOptions: supportedStatuses.map(status => ({
              value: camelToSnake(status),
              label: intl.formatMessage({
                id: `containers.annotationList.statuses.${snakeToCamel(status)}`,
              }),
            })),
            filterable: true,
          }
        : undefined,
      documentColumn !== undefined
        ? {
            ...commonMetaColumnProps,
            metaName: 'original_file_name',
            field: 'original_file_name' satisfies MetaField,
            headerName: intl.formatMessage({
              id: 'components.documentOverview.document__original_file_name',
            }),
            width: 270,
            renderCell: params => (
              <RenderFilenameCell
                {...params}
                onMouseOver={() =>
                  documentColumn.setRowForPreview?.(params.row)
                }
                onMouseOut={() => documentColumn.setRowForPreview?.(undefined)}
              />
            ),
            operators: stringFieldOperators,
            filterable: true,
          }
        : undefined,
      workflowFilenameColumn !== undefined
        ? {
            ...commonMetaColumnProps,
            metaName: 'original_file_name',
            field: 'original_file_name' satisfies MetaField,
            headerName: intl.formatMessage({
              id: 'components.documentOverview.document__original_file_name',
            }),
            width: 270,
            renderCell: params => (
              <RenderWorkflowFilenameCell
                {...params}
                to={{
                  pathname: `/requests/${params.id}`,
                }}
                onMouseOver={() =>
                  workflowFilenameColumn.setRowForPreview?.(params.row)
                }
                onMouseOut={() =>
                  workflowFilenameColumn.setRowForPreview?.(undefined)
                }
              />
            ),
            operators: stringFieldOperators,
            filterable: true,
          }
        : undefined,
      detailsColumn !== undefined
        ? {
            ...commonMetaColumnProps,
            metaName: 'details',
            field: 'details' satisfies MetaField,
            headerName: intl.formatMessage({
              id: 'components.documentOverview.details',
            }),
            width: 150,
            filterable: true,
            valueOptions: R.filter(
              [
                {
                  label: intl.formatMessage({
                    id: 'components.documentOverview.details.valueOptions.automated',
                  }),
                  value: detailsColumnOptionKeys.automated,
                },
                {
                  label: intl.formatMessage({
                    id: 'components.documentOverview.details.valueOptions.automaticallyRejected',
                  }),
                  value: detailsColumnOptionKeys.automatically_rejected,
                },
                eInvoicingEnabled && {
                  label: intl.formatMessage({
                    id: 'components.documentOverview.details.valueOptions.einvoice',
                  }),
                  value: detailsColumnOptionKeys.einvoice,
                },
                {
                  label: intl.formatMessage({
                    id: 'components.documentOverview.details.valueOptions.emails',
                  }),
                  value: detailsColumnOptionKeys.emails,
                },
                {
                  label: intl.formatMessage({
                    id: 'components.documentOverview.details.valueOptions.attachments',
                  }),
                  value: detailsColumnOptionKeys.attachments,
                },
                {
                  label: intl.formatMessage({
                    id: 'components.documentOverview.details.valueOptions.duplicates',
                  }),
                  value: detailsColumnOptionKeys.duplicates,
                },
                {
                  label: intl.formatMessage({
                    id: 'components.documentOverview.details.valueOptions.edited',
                  }),
                  value: detailsColumnOptionKeys.edit,
                },
                {
                  label: intl.formatMessage({
                    id: 'components.documentOverview.details.valueOptions.hasSuggestedEdit',
                  }),
                  value: detailsColumnOptionKeys.has_suggested_edit,
                },
                {
                  label: intl.formatMessage({
                    id: 'components.documentOverview.details.valueOptions.hasNote',
                  }),
                  value: detailsColumnOptionKeys.has_note,
                },
                {
                  label: intl.formatMessage({
                    id: 'components.documentOverview.details.valueOptions.excludedFromTraining',
                  }),
                  value: detailsColumnOptionKeys.excluded_from_training,
                },
              ],
              R.isTruthy
            ),
            resizable: true,
            hideable: false,
            operators: detailsOperators,
            renderCell: props => {
              return (
                <RenderDetails
                  {...props}
                  hasWorkflowRun={props.row.hasWorkflowRun}
                  isExcludedFromTraining={props.row.training_enabled === false}
                  handleSelectAnnotation={detailsColumn.handleSelectAnnotation}
                />
              );
            },
            sortable: false,
          }
        : undefined,
      relationsColumn !== undefined && customRelationsEnabled
        ? {
            ...commonMetaColumnProps,
            metaName: 'relations',
            field: 'relations' satisfies MetaField,
            headerName: intl.formatMessage({
              id: 'components.documentOverview.relations',
            }),
            width: 200,
            renderCell: ({ row }: { row: RowType }) => (
              <CustomRelationBadges
                annotationUrl={row.url}
                customRelations={row.customRelations ?? []}
                onOpenRelation={relationsColumn.handleSelectAnnotation}
              />
            ),
            filterable: false,
            sortable: false,
            operators: [],
          }
        : undefined,
      queueColumn !== undefined
        ? {
            ...commonMetaColumnProps,
            visible: queueColumn.level !== 'queue',
            metaName: 'queue',
            field: 'queue' satisfies MetaField,
            headerName: intl.formatMessage({
              id: 'components.documentOverview.queue',
            }),
            width: 300,
            renderCell: cellProps => (
              <WorkspacesQueuesCell
                {...cellProps}
                workspacesProps={{
                  workspacesWithQueuesMap,
                  workspacesAreLoading,
                }}
              />
            ),
            operators: queueSelectOperators,
            valueOptions: sortValueOptions(
              Object.values(workspacesWithQueuesMap).flatMap(item => ({
                label: item.queueName,
                value: `${item.queueId}`,
              }))
            ),
            filterable: true,
            areValuesLoading: workspacesAreLoading,
            disableWithNoValues: true,
          }
        : undefined,
      labelsEnabled && labelsColumn !== undefined
        ? {
            ...commonMetaColumnProps,
            metaName: 'labels',
            field: 'labels' satisfies MetaField,
            headerName: intl.formatMessage({
              id: 'components.documentOverview.labels',
            }),
            width: 250,
            renderCell: ({ row }: { row: RowType }) => (
              <LabelsColumn
                restrictedAccess={row.restricted_access}
                labels={R.pipe(
                  row.labels.map(labelUrl => allLabels?.[labelUrl]),
                  R.filter(R.isTruthy)
                )}
              />
            ),
            operators: multiSelectOperators,
            valueOptions: sortValueOptions(
              Object.values(allLabels ?? {}).map(label => ({
                value: `${label.id}`,
                label: label.name,
              }))
            ),
            valueGetter: (value: Array<string>) => value.join(', '),
            filterable: true,
            areValuesLoading: allLabelsAreLoading,
            disableWithNoValues: true,
            allowedMultipleOperatorInstances: ['isAnyOf'],
          }
        : undefined,
      trainingColumn !== undefined
        ? ({
            ...commonMetaColumnProps,
            metaName: 'training_enabled',
            field: 'training_enabled',
            headerName: intl.formatMessage({
              id: 'components.documentOverview.trainingEnabled',
            }),
            width: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: RenderTrainingStatus,
            filterable: false,
            sortable: false,
          } as unknown as MetaColDef<RowType>)
        : undefined,
    ],
    R.filter(R.isDefined)
  );

  return { columns };
};
